import { WarningIcon } from '@eleveo/icons-react';
import { differenceInSeconds, format, isAfter, isBefore } from 'date-fns';
import { format as formatWithTZ, toDate, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import {
  DataLabelsFormatterContextObject,
  Point,
  TooltipFormatterCallbackFunction,
  TooltipFormatterContextObject,
} from 'highcharts';
import { TFunction } from 'i18next';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { v4 } from 'uuid';

import { AdherenceHistoryBadge } from './AdherenceHistoryBadge';
import { barChartSeriesConfig, ganttChartSeriesConfig, radioFilterTileOptionsObj } from './config';
import {
  AdherenceHistoryRadioInputs,
  GanttChartElement,
  GanttChartOOAElement,
  GanttChartRealityElement,
  GanttChartScheduleElement,
  TransformedGanttChartData,
} from './types';
import { translateScheduleElementName } from '../../components/MySchedule/transformData';
import { AdherenceHistoryBarChart, AdherenceHistoryGanttChart, ScheduleElement } from '../../graphql/components';
import { RadioFilter, RangeFilter } from '../../types';
import { recordBy } from '../../utils/array';
import { formatScheduleElementRGBAColor } from '../../utils/chart';
import { UTC_TIMEZONE_NAME } from '../../utils/date';
import { formatSecondsToReadableForm } from '../../utils/dateRange';
import { approximateScheduleElementNameSize } from '../Scheduling/utils/transformScheduleElementsData';

export const barChartTooltipFormatter = (
  t: TFunction,
  locale: string,
  data?: AdherenceHistoryBarChart
): TooltipFormatterCallbackFunction =>
  function formatter() {
    const points = this.points as TooltipFormatterContextObject[];
    const xPosition = this.x;

    const currentData = data?.dataPerDay[xPosition];

    if (!currentData || points?.length !== barChartSeriesConfig.length) {
      return false;
    }

    const { earlyLeaves, lateArrivals, outOfAdhEventsCount, scheduledTime, scheduledWorkingTime, outOfAdhTimeTotal } =
      currentData;

    const inAdherence = points[0].y;
    const conformance = points[1].y;

    return `
      <table class="adherence-history__bar-chart__tooltip-container" data-test-id="adherence-history-detail:bar-chart-tooltip">
        <tbody>
          <tr>
            <td>${t(
              'scheduleAdherence.scheduledTime'
            )}:</td><td data-test-id="adherence-history-detail:bar-chart-tooltip:scheduled-time">${formatSecondsToReadableForm(
      scheduledTime,
      t
    )}</td>
            <td>${t(
              'scheduleAdherence.scheduledWorkTime'
            )}:</td><td data-test-id="adherence-history-detail:bar-chart-tooltip:scheduled-work-time">${formatSecondsToReadableForm(
      scheduledWorkingTime,
      t
    )}</td>
          </tr>
          <tr>
            <td>${t(
              'scheduleAdherence.outOfAdherenceEvents'
            )}:</td><td data-test-id="adherence-history-detail:bar-chart-tooltip:out-of-adherence-events">${renderToStaticMarkup(
      <AdherenceHistoryBadge kind="primary">{outOfAdhEventsCount}</AdherenceHistoryBadge>
    )}</td>
            <td>${t(
              'scheduleAdherence.timeOutOfAdherence'
            )}:</td><td data-test-id="adherence-history-detail:bar-chart-tooltip:time-out-of-adherence">${formatSecondsToReadableForm(
      outOfAdhTimeTotal,
      t
    )}</td>
          </tr>
          <tr>
            <td>${t(
              'scheduleAdherence.inAdherence'
            )}:</td><td data-test-id="adherence-history-detail:bar-chart-tooltip:in-adherence">${(
      inAdherence / 100
    ).toLocaleString(locale, { maximumFractionDigits: 2, style: 'percent' })}</td>
            <td>${t(
              'scheduleAdherence.conformance'
            )}:</td><td data-test-id="adherence-history-detail:bar-chart-tooltip:conformance">${(
      conformance / 100
    ).toLocaleString(locale, { maximumFractionDigits: 2, style: 'percent' })}</td>
          </tr>
          <tr>
            <td>${t('scheduleAdherence.lateArrivals', {
              count: 1,
            })}:</td><td class="text-uppercase" data-test-id="adherence-history-detail:bar-chart-tooltip:late-arrival">${renderToStaticMarkup(
      <AdherenceHistoryBadge displayValueAsWord kind="danger">
        {Number(lateArrivals)}
      </AdherenceHistoryBadge>
    )}</td>
            <td>${t('scheduleAdherence.earlyLeaves', {
              count: 1,
            })}:</td><td class="text-uppercase" data-test-id="adherence-history-detail:bar-chart-tooltip:early-leave">${renderToStaticMarkup(
      <AdherenceHistoryBadge displayValueAsWord kind="danger">
        {Number(earlyLeaves)}
      </AdherenceHistoryBadge>
    )}</td>
          </tr>
        </tbody>
      </table>
    `;
  };

// Could be used also for listing Active Filters under the header content
export const renderRangeTileSubtitleHeader = (
  rangeValues: RangeFilter,
  t: TFunction,
  locale: string,
  isPercentage = true
) => {
  const minValue = rangeValues?.min;
  const maxValue = rangeValues?.max;

  const minLabel =
    typeof minValue === 'number'
      ? `${t('filter.min')} ${
          isPercentage
            ? (minValue / 100).toLocaleString(locale, { maximumFractionDigits: 2, style: 'percent' })
            : minValue.toLocaleString(locale)
        }`
      : '';
  const maxLabel =
    typeof maxValue === 'number'
      ? `${t('filter.max')} ${
          isPercentage
            ? (maxValue / 100).toLocaleString(locale, { maximumFractionDigits: 2, style: 'percent' })
            : maxValue.toLocaleString(locale)
        }`
      : '';

  return `${minLabel} ${maxLabel}`.trim();
};

// Could be used also for listing Active Filters under the header content
export const renderRadioTileSubtitleHeader = (
  key: keyof AdherenceHistoryRadioInputs,
  rangeValue: RadioFilter,
  t: TFunction
) => {
  if (!rangeValue) {
    return '';
  }

  const radioOptions = radioFilterTileOptionsObj(t)[key];
  const selectedOptionIndex = radioOptions.findIndex(({ value }) => value === rangeValue);

  return rangeValue && selectedOptionIndex !== -1 ? radioOptions[selectedOptionIndex].label : '';
};

export const mapGanttChartData = (
  startDateTime: number,
  endDateTime: number,
  timezone: string,
  t: TFunction,
  adherenceData?: AdherenceHistoryGanttChart,
  scheduleElements?: ScheduleElement[]
): TransformedGanttChartData => {
  const data: TransformedGanttChartData['data'] = {
    outOfAdherence: [],
    reality: [],
    scheduled: [],
  };
  const firstElement =
    adherenceData?.agentActivityItems?.[0] ??
    adherenceData?.outOfAdherenceItems?.[0] ??
    adherenceData?.scheduleItems?.[0];

  if (!adherenceData || !scheduleElements || !firstElement) {
    return { data, maxX: null, minX: null };
  }

  // All dates must be present in UTC timezone
  let firstElementStart: Date = zonedTimeToUtc(endDateTime, timezone);
  let lastElementEnd: Date = zonedTimeToUtc(startDateTime, timezone);

  const scheduleElementsRecordObj = recordBy(scheduleElements, 'id');

  const updateBoundaries = (startDate: Date, endDate: Date) => {
    if (!firstElementStart || isBefore(startDate, firstElementStart)) {
      firstElementStart = startDate;
    }

    if (!lastElementEnd || isAfter(endDate, lastElementEnd)) {
      lastElementEnd = endDate;
    }
  };

  adherenceData.scheduleItems.forEach((scheduleItem) => {
    const sourceScheduleElement = scheduleElementsRecordObj[scheduleItem.scheduleElementId];
    const startDate = toDate(scheduleItem.dateTimeRange.startDateTime, { timeZone: UTC_TIMEZONE_NAME });
    const endDate = toDate(scheduleItem.dateTimeRange.endDateTime, { timeZone: UTC_TIMEZONE_NAME });

    const start = Number(format(startDate, 'T'));
    const end = Number(format(endDate, 'T'));

    const elementName = translateScheduleElementName(t, sourceScheduleElement);

    if (sourceScheduleElement) {
      data.scheduled.push({
        end,
        start,
        color: formatScheduleElementRGBAColor(sourceScheduleElement.color),
        key: v4(),
        name: elementName,
        nameSize: approximateScheduleElementNameSize(elementName),
        paid: sourceScheduleElement.paid,
        paidTimeOff: sourceScheduleElement.paidTimeOff,
        productive: sourceScheduleElement.productive,
        scheduleElementId: scheduleItem.scheduleElementId,
        y: 1,
      });
      updateBoundaries(startDate, endDate);
    }
  });

  adherenceData.agentActivityItems.forEach((activityItem, index) => {
    const startDate = toDate(activityItem.dateTimeRange.startDateTime, { timeZone: UTC_TIMEZONE_NAME });
    const endDate = toDate(activityItem.dateTimeRange.endDateTime, { timeZone: UTC_TIMEZONE_NAME });

    const start = Number(format(startDate, 'T'));
    const end = Number(format(endDate, 'T'));

    const colorSchemeConfigIndex = activityItem.mappedToActivity ? 2 : 0; // if element is not mapped to activity, it should be colored with red color - same way as OOA events do

    data.reality.push({
      end,
      start,
      color: ganttChartSeriesConfig[colorSchemeConfigIndex].color[index % 2],
      key: v4(),
      mappedToActivity: activityItem.mappedToActivity,
      name: activityItem.eleveoStatusName,
      nameSize: approximateScheduleElementNameSize(activityItem.eleveoStatusName, false), // not all characters are upper-cased!
      y: 2,
    });
    updateBoundaries(startDate, endDate);
  });

  adherenceData.outOfAdherenceItems.forEach((outOfAdherenceItem, index) => {
    const sourceScheduleElement = scheduleElementsRecordObj[outOfAdherenceItem.scheduleElementId];
    const startDate = toDate(outOfAdherenceItem.dateTimeRange.startDateTime, { timeZone: UTC_TIMEZONE_NAME });
    const endDate = toDate(outOfAdherenceItem.dateTimeRange.endDateTime, { timeZone: UTC_TIMEZONE_NAME });

    const start = Number(format(startDate, 'T'));
    const end = Number(format(endDate, 'T'));

    if (sourceScheduleElement) {
      const elementName = translateScheduleElementName(t, sourceScheduleElement);

      data.outOfAdherence.push({
        end,
        start,
        activityColor: formatScheduleElementRGBAColor(sourceScheduleElement.color),
        color: ganttChartSeriesConfig[0].color[index % 2],
        key: v4(),
        name: elementName,
        nameSize: approximateScheduleElementNameSize(elementName),
        statusName: outOfAdherenceItem.eleveoStatusName,
        y: 0,
      });
      updateBoundaries(startDate, endDate);
    }
  });

  return { data, minX: firstElementStart, maxX: lastElementEnd };
};

export function formatScheduleElementLabel(toUpperCase: boolean) {
  return function formatter(this: DataLabelsFormatterContextObject): string {
    const element = this.point as Point & GanttChartElement;
    let elementName = element.name;
    if (toUpperCase) {
      elementName = elementName.toUpperCase();
    }

    // @ts-expect-error: incompatible type 😥
    const elementSizeInPx = element.shapeArgs?.width ?? 0;
    const sizePerLetter = Math.floor(element.nameSize / element.name.length);

    // hide label as it wouldn't fit into element size anyway
    if (elementSizeInPx <= sizePerLetter * 2) {
      return '';
    }

    // show first letter of element's name
    if (elementSizeInPx / sizePerLetter <= 2.5) {
      return elementName.slice(0, 1);
    }

    // show first two letters of element's name
    if (elementSizeInPx <= element.nameSize) {
      return elementName.slice(0, 2);
    }

    // show full element's name
    return elementName;
  };
}

export const ganttChartOtherSeriesTooltipFormatter = (
  point: Point & (GanttChartScheduleElement | GanttChartRealityElement),
  t: TFunction,
  timezone: string,
  seriesId: string,
  dataTestIdSlug: string,
  localeObj?: Locale
) => {
  const isScheduledType = seriesId === 'scheduled';
  const timeFormat = isScheduledType ? t('date.formats.timeShort') : t('date.formats.timeWithSecondsShort');
  const startTime = formatWithTZ(utcToZonedTime(point.start, timezone), timeFormat, {
    locale: localeObj,
    timeZone: timezone,
  });
  const endTime = formatWithTZ(utcToZonedTime(point.end, timezone), timeFormat, {
    locale: localeObj,
    timeZone: timezone,
  });
  const durationInSeconds = differenceInSeconds(point.end, point.start);
  const pointTypeName = t(isScheduledType ? 'scheduleAdherence.scheduleActivity' : 'person.personsStatus');

  const renderedWarning =
    !isScheduledType && !(point as GanttChartRealityElement).mappedToActivity
      ? `<p class="d-flex align-items-center" data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:acd-status-not-mapped-warning">${renderToStaticMarkup(
          <WarningIcon className="mr-1" color="#d80225" />
        )}${t('scheduleAdherence.acdStatusNotMappedToAnyActivity')}</p>`
      : '';

  return `
    <div class="highcharts-tooltip-inner" data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip" style="border-color: ${
    point.color
  }">
      <p><label>${pointTypeName}:</label><br>
        ${
          /* Name of reality items are upper-cased, so we need to lower-case them first and capitalize by css afterwards */ ''
        }
        <b data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:name" style="text-transform: capitalize;">${point.name.toLowerCase()}</b>
      </p><p><label>${t(
        'time.duration'
      )}: <b data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:duration">${startTime} - ${endTime}</b></label><br>
        <b><big data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:duration-time">
          ${formatSecondsToReadableForm(durationInSeconds, t)}
      </big></b></p>
      ${renderedWarning}
    </div>`;
};

export const ganttChartTooltipFormatter = (
  t: TFunction,
  timezone: string,
  dataTestIdSlug: string,
  localeObj?: Locale
): TooltipFormatterCallbackFunction =>
  function formatter() {
    let point = this.point as Point & (GanttChartOOAElement | GanttChartRealityElement | GanttChartScheduleElement);

    if (!point) {
      return false;
    }

    // @ts-expect-error: incompatible type 😥
    const seriesId = point.series.userOptions.id;

    if (seriesId === 'scheduled') {
      return ganttChartOtherSeriesTooltipFormatter(
        point as Point & GanttChartScheduleElement,
        t,
        timezone,
        seriesId,
        dataTestIdSlug,
        localeObj
      );
    }

    if (seriesId === 'reality') {
      return ganttChartOtherSeriesTooltipFormatter(
        point as Point & GanttChartRealityElement,
        t,
        timezone,
        seriesId,
        dataTestIdSlug,
        localeObj
      );
    }

    point = this.point as Point & GanttChartOOAElement;

    return `
      <table class="adherence-history__gantt-chart__tooltip-container" data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip">
        <tbody>
          <tr>
            <td data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:schedule-activity">${t(
      'scheduleAdherence.scheduleActivity'
    )}:</td><td class="d-flex align-items-center"><span class="schedule-item-indicator" style="background-color: ${
      point.activityColor
    };"></span>${point.name}</td>
          </tr>
          <tr>
            <td data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:person-s-status">${t(
      'person.personsStatus'
    )}:</td><td>${point.statusName}</td>
          </tr>
          <tr>
            <td data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:out-of-adherence-start-time">${t(
      'scheduleAdherence.ooaStart'
    )}:</td><td>${format(utcToZonedTime(point.start, timezone), t('date.formats.time'), {
      locale: localeObj,
    })}</td>
          </tr>
          <tr>
            <td data-test-id="${dataTestIdSlug}-detail:gantt-chart-tooltip:out-of-adherence-duration-time">${t(
      'scheduleAdherence.ooaDuration'
    )}:</td><td>${formatSecondsToReadableForm(differenceInSeconds(point.end, point.start), t)}</td>
          </tr>
        </tbody>
      </table>
    `;
  };
